import { useUser } from "@clerk/tanstack-react-start";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
  BuildingIcon,
  EditIcon,
  MoreHorizontalIcon,
  PlusIcon,
  RefreshCwIcon,
  TrashIcon,
  UserPlusIcon,
  UsersIcon,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DataTable } from "@/components/data-table/data-table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/admin/organizations")({
  component: AdminOrganizations,
  ssr: false,
});

type Organization = {
  id: string;
  name: string;
  slug: string;
  plan?: string;
  createdAt: string;
  membersCount?: number;
  imageUrl?: string;
};

type AdminUser = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  emailAddresses: Array<{ emailAddress: string }>;
  publicMetadata?: {
    admin?: boolean;
  };
};

function AdminOrganizations() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { user: currentUser } = useUser();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteOrgId, setDeleteOrgId] = useState<string | null>(null);
  const [orgForMembership, setOrgForMembership] = useState<Organization | null>(
    null
  );
  const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
  const [newMemberUserId, setNewMemberUserId] = useState<string>("");
  const [newMemberRole, setNewMemberRole] = useState<string>("org:member");

  const { data: organizations = [] } = useSuspenseQuery<Organization[]>({
    queryKey: ["admin", "organizations"],
    queryFn: async () => {
      const response = await fetch("/api/admin/organizations", {
        credentials: "include",
      });
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(errorData.error || "Failed to fetch organizations");
      }
      const data = (await response.json()) as {
        organizations?: Organization[];
      };
      if (!Array.isArray(data.organizations)) {
        throw new Error("Received invalid organizations payload");
      }
      return data.organizations;
    },
  });

  const { data: users = [] } = useSuspenseQuery<AdminUser[]>({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const response = await fetch("/api/admin/users", {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }
      const data = (await response.json()) as {
        users?: AdminUser[];
      };
      if (!Array.isArray(data.users)) {
        throw new Error("Received invalid users payload");
      }
      return data.users;
    },
  });

  const createOrgMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      plan: string;
      createdBy: string;
    }) => {
      const response = await fetch("/api/admin/organizations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(errorData.error || "Failed to create organization");
      }
      return response.json();
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setIsCreateDialogOpen(false);
      toast.success("Organization created successfully");

      // Invalidate the router to refresh the TeamSwitcher's organization list
      await router.invalidate();
    },
    onError: (error: Error) => {
      console.error("Failed to create organization:", error);
      toast.error("Failed to create organization");
    },
  });

  const updateOrgMutation = useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: string;
      name: string;
      plan: string;
    }) => {
      const response = await fetch(`/api/admin/organizations/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Failed to update organization");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "organizations"] });
      setIsEditDialogOpen(false);
      setSelectedOrg(null);
      toast.success("Organization updated successfully");
    },
    onError: () => {
      toast.error("Failed to update organization");
    },
  });

  const deleteOrgMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/admin/organizations/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to delete organization");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "organizations"] });
      toast.success("Organization deleted successfully");
      setDeleteOrgId(null);
    },
    onError: () => {
      toast.error("Failed to delete organization");
    },
  });

  const syncOrgMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/admin/organizations/${id}/sync`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to sync organization");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "organizations"] });
      toast.success("Organization synced with database");
    },
    onError: () => {
      toast.error("Failed to sync organization");
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: async ({
      organizationId,
      userId,
      role,
    }: {
      organizationId: string;
      userId: string;
      role: string;
    }) => {
      const response = await fetch("/api/admin/memberships", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ organizationId, userId, role }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Failed to add member" }));
        throw new Error(errorData.error || "Failed to add member");
      }

      return response.json();
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("User added to organization");
      setIsAddMemberDialogOpen(false);
      setOrgForMembership(null);
      setNewMemberUserId("");
      setNewMemberRole("org:member");
      await router.invalidate();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to add member");
    },
  });

  const columns: ColumnDef<Organization>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <BuildingIcon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: "slug",
      header: "Slug",
      cell: ({ getValue }) => (
        <code className="rounded bg-muted px-2 py-1 text-sm">
          {getValue() as string}
        </code>
      ),
    },
    {
      accessorKey: "plan",
      header: "Plan",
      cell: ({ row }) => {
        const plan = row.original.plan || "Starter";
        const planColors = {
          Starter: "secondary",
          Growth: "default",
          Pro: "destructive",
        } as const;
        return (
          <Badge
            variant={planColors[plan as keyof typeof planColors] || "secondary"}
          >
            {plan}
          </Badge>
        );
      },
    },
    {
      accessorKey: "membersCount",
      header: "Members",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <UsersIcon className="h-4 w-4 text-muted-foreground" />
          <span>{row.original.membersCount || 0}</span>
        </div>
      ),
    },
    {
      accessorKey: "createdAt",
      header: "Created",
      cell: ({ getValue }) =>
        new Date(getValue() as string).toLocaleDateString(),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="h-8 w-8 p-0" variant="ghost">
              <span className="sr-only">Open menu</span>
              <MoreHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => {
                setSelectedOrg(row.original);
                setIsEditDialogOpen(true);
              }}
            >
              <EditIcon className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setOrgForMembership(row.original);
                setIsAddMemberDialogOpen(true);
                setNewMemberUserId("");
                setNewMemberRole("org:member");
              }}
            >
              <UserPlusIcon className="mr-2 h-4 w-4" />
              Add Member
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => syncOrgMutation.mutate(row.original.id)}
            >
              <RefreshCwIcon className="mr-2 h-4 w-4" />
              Sync to Database
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteOrgId(row.original.id)}
            >
              <TrashIcon className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-bold text-3xl">Organizations</h1>
          <p className="text-muted-foreground">
            Manage all organizations in the system
          </p>
        </div>
        <Dialog onOpenChange={setIsCreateDialogOpen} open={isCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <PlusIcon className="mr-2 h-4 w-4" />
              Create Organization
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                createOrgMutation.mutate({
                  name: formData.get("name") as string,
                  plan: formData.get("plan") as string,
                  createdBy: formData.get("createdBy") as string,
                });
              }}
            >
              <DialogHeader>
                <DialogTitle>Create Organization</DialogTitle>
                <DialogDescription>
                  Create a new organization in Clerk and sync it to the database
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Acme Corporation"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="plan">Plan</Label>
                  <Select defaultValue="Starter" name="plan" required>
                    <SelectTrigger id="plan">
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Starter">Starter</SelectItem>
                      <SelectItem value="Growth">Growth</SelectItem>
                      <SelectItem value="Pro">Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="createdBy">Created By</Label>
                  <Select name="createdBy" required>
                    <SelectTrigger id="createdBy">
                      <SelectValue placeholder="Select a user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users
                        ?.filter((user) => user.id !== currentUser?.id)
                        .map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.firstName} {user.lastName} (
                            {user.emailAddresses[0]?.emailAddress})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => setIsCreateDialogOpen(false)}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button disabled={createOrgMutation.isPending} type="submit">
                  {createOrgMutation.isPending ? "Creating..." : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable columns={columns} data={organizations} />

      {orgForMembership && (
        <Dialog
          onOpenChange={(open) => {
            setIsAddMemberDialogOpen(open);
            if (!open) {
              setOrgForMembership(null);
              setNewMemberUserId("");
              setNewMemberRole("org:member");
            }
          }}
          open={isAddMemberDialogOpen}
        >
          <DialogContent>
            <form
              onSubmit={(event) => {
                event.preventDefault();

                if (!newMemberUserId) {
                  toast.error("Select a user to add");
                  return;
                }

                addMemberMutation.mutate({
                  organizationId: orgForMembership.id,
                  userId: newMemberUserId,
                  role: newMemberRole,
                });
              }}
            >
              <DialogHeader>
                <DialogTitle>Add Member</DialogTitle>
                <DialogDescription>
                  Choose a user to add to{" "}
                  <span className="font-medium">{orgForMembership.name}</span>{" "}
                  and assign a role.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="new-member-user">User</Label>
                  <Select
                    onValueChange={setNewMemberUserId}
                    value={newMemberUserId}
                  >
                    <SelectTrigger id="new-member-user">
                      <SelectValue placeholder="Select a user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.firstName} {user.lastName} (
                          {user.emailAddresses[0]?.emailAddress})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="new-member-role">Role</Label>
                  <Select
                    onValueChange={setNewMemberRole}
                    value={newMemberRole}
                  >
                    <SelectTrigger id="new-member-role">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="org:member">Member</SelectItem>
                      <SelectItem value="org:admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => {
                    setIsAddMemberDialogOpen(false);
                    setOrgForMembership(null);
                    setNewMemberUserId("");
                    setNewMemberRole("org:member");
                  }}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button disabled={addMemberMutation.isPending} type="submit">
                  {addMemberMutation.isPending ? "Adding..." : "Add"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {selectedOrg && (
        <Dialog onOpenChange={setIsEditDialogOpen} open={isEditDialogOpen}>
          <DialogContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                updateOrgMutation.mutate({
                  id: selectedOrg.id,
                  name: formData.get("name") as string,
                  plan: formData.get("plan") as string,
                });
              }}
            >
              <DialogHeader>
                <DialogTitle>Edit Organization</DialogTitle>
                <DialogDescription>
                  Update organization details in Clerk
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    defaultValue={selectedOrg.name}
                    id="edit-name"
                    name="name"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-plan">Plan</Label>
                  <Select
                    defaultValue={selectedOrg.plan || "Starter"}
                    name="plan"
                    required
                  >
                    <SelectTrigger id="edit-plan">
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Starter">Starter</SelectItem>
                      <SelectItem value="Growth">Growth</SelectItem>
                      <SelectItem value="Pro">Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => {
                    setIsEditDialogOpen(false);
                    setSelectedOrg(null);
                  }}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button disabled={updateOrgMutation.isPending} type="submit">
                  {updateOrgMutation.isPending ? "Updating..." : "Update"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog
        onOpenChange={(open) => !open && setDeleteOrgId(null)}
        open={!!deleteOrgId}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              organization{" "}
              <span className="font-semibold">
                "
                {deleteOrgId &&
                  organizations.find((org) => org.id === deleteOrgId)?.name}
                "
              </span>{" "}
              and remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                deleteOrgId && deleteOrgMutation.mutate(deleteOrgId)
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
